import { useState, useCallback, useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Sparkle, CheckCircle, ArrowRight, ArrowLeft, ChatCircle, EnvelopeSimple, BookOpenText, FirstAidKit } from "@phosphor-icons/react";
import AppNav from "../components/AppNav";
import PageMeta from "../components/PageMeta";
import JsonLd, { softwareAppSchema } from "../components/JsonLd";
import { useAuth } from "../contexts/AuthContext";
import LoginModal from "../components/LoginModal";
import { apiUrl } from "../config/api";
import { QUIZZES, getQuizBySlug } from "../data/quizData";
import type { QuizData, QuizResultTier } from "../data/quizData";

type Phase = "picker" | "intro" | "questions" | "results";

interface QuizResults {
  tier: QuizResultTier;
  symptoms: string[];
  breakdown: Record<string, number>;
}

function calculateResults(
  quiz: QuizData,
  answers: Map<number, string | string[]>
): QuizResults {
  const counts: Record<string, number> = { A: 0, B: 0, C: 0, D: 0 };
  const checkboxIds = new Set(quiz.checkboxQuestionIds);

  for (const [qId, value] of answers) {
    if (checkboxIds.has(qId)) continue;
    if (typeof value === "string" && value in counts) {
      counts[value]++;
    }
  }

  // D-biased tiebreak (same as original)
  const maxCount = Math.max(counts.A, counts.B, counts.C, counts.D);
  let tierKey = "A";
  if (counts.D === maxCount) tierKey = "D";
  else if (counts.C === maxCount) tierKey = "C";
  else if (counts.B === maxCount) tierKey = "B";

  const tier = quiz.resultTiers.find((t) => t.key === tierKey) ?? quiz.resultTiers[0];

  // Collect checkbox symptoms
  const symptoms: string[] = [];
  for (const cbId of quiz.checkboxQuestionIds) {
    const val = answers.get(cbId);
    if (Array.isArray(val)) {
      for (const s of val) {
        if (s.toLowerCase() !== "none of the above") {
          symptoms.push(s);
        }
      }
    }
  }

  return { tier, symptoms, breakdown: counts };
}

function formatSymptomNote(template: string, symptoms: string[]): string {
  const count = symptoms.length;
  const examples = symptoms.slice(0, 3).join(", ");
  return template.replace("{count}", String(count)).replace("{examples}", examples);
}

// --- Sub-components ---

function PickerPhase({
  onSelectQuiz,
}: {
  onSelectQuiz: (slug: string) => void;
}) {
  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <div className="text-center mb-10">
        <div className="text-4xl mb-3">✨</div>
        <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-800 dark:text-gray-100 mb-4">
          Free Health Quizzes
        </h1>
        <p className="text-lg text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
          Take a self-check quiz to understand your symptoms and get personalized insights
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {QUIZZES.map((quiz) => (
          <button
            key={quiz.slug}
            onClick={() => onSelectQuiz(quiz.slug)}
            className={`bg-gradient-to-r ${quiz.gradientFrom} ${quiz.gradientTo} rounded-2xl shadow-xl p-8 text-white hover:shadow-2xl transition-all text-left`}
          >
            <div className="text-4xl mb-4">{quiz.emoji}</div>
            <h3 className="text-xl sm:text-2xl font-bold mb-3">{quiz.title}</h3>
            <p className="text-white/90 mb-6">{quiz.subtitle}</p>
            <div className="inline-flex items-center space-x-2 font-semibold">
              <span>Take Quiz</span>
              <ArrowRight size={20} weight="bold" />
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

function IntroPhase({
  quiz,
  onStart,
  onBack,
}: {
  quiz: QuizData;
  onStart: () => void;
  onBack: () => void;
}) {
  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <button
        onClick={onBack}
        className="inline-flex items-center text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 mb-6 transition-colors"
      >
        <ArrowLeft size={18} className="mr-1" />
        Back to quizzes
      </button>

      <div className="text-center mb-8">
        <div className="text-5xl mb-4">{quiz.emoji}</div>
        <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-800 dark:text-gray-100 mb-3">
          {quiz.title}
        </h1>
        <p className="text-lg text-gray-600 dark:text-gray-400 mb-2">{quiz.subtitle}</p>
        <p className="text-sm text-gray-500 dark:text-gray-500">
          {quiz.questions.length} questions &middot; ~3 min
        </p>
      </div>

      <div className="bg-pink-50 dark:bg-pink-950 rounded-2xl p-6 sm:p-8 mb-8 border border-pink-200 dark:border-pink-800">
        <p className="text-gray-700 dark:text-gray-300 whitespace-pre-line leading-relaxed">
          {quiz.description}
        </p>
      </div>

      <div className="text-center">
        <button
          onClick={onStart}
          className="px-8 py-4 bg-gradient-to-r from-orange-500 to-pink-500 text-white font-semibold rounded-lg hover:from-orange-600 hover:to-pink-600 transition-all shadow-lg hover:shadow-xl text-lg"
        >
          Start Quiz
        </button>
      </div>
    </div>
  );
}

function QuestionsPhase({
  quiz,
  currentIndex,
  answers,
  onAnswer,
  onNext,
  onPrevious,
  onComplete,
}: {
  quiz: QuizData;
  currentIndex: number;
  answers: Map<number, string | string[]>;
  onAnswer: (questionId: number, value: string | string[]) => void;
  onNext: () => void;
  onPrevious: () => void;
  onComplete: () => void;
}) {
  const question = quiz.questions[currentIndex];
  const currentAnswer = answers.get(question.id) ?? null;
  const isLast = currentIndex === quiz.questions.length - 1;

  const isAnswered = question.isCheckbox ? true : currentAnswer !== null;

  const handleOptionClick = (optionValue: string) => {
    if (question.isCheckbox) {
      const current = Array.isArray(currentAnswer) ? (currentAnswer as string[]) : [];
      const text = optionValue;
      if (current.includes(text)) {
        onAnswer(question.id, current.filter((v) => v !== text));
      } else {
        onAnswer(question.id, [...current, text]);
      }
    } else {
      onAnswer(question.id, optionValue);
    }
  };

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      {/* Progress bar */}
      <div className="mb-6">
        <div className="flex justify-between text-sm text-gray-500 dark:text-gray-400 mb-2">
          <span>Question {currentIndex + 1} of {quiz.questions.length}</span>
        </div>
        <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
          <div
            className="bg-gradient-to-r from-orange-500 to-pink-500 h-2 rounded-full transition-all duration-300"
            style={{ width: `${((currentIndex + 1) / quiz.questions.length) * 100}%` }}
          />
        </div>
      </div>

      {/* Fixed-height question container */}
      <div className="min-h-[420px] flex flex-col">
        <div key={currentIndex} className="animate-fadeIn flex-1 flex flex-col">
          <div className="bg-pink-50 dark:bg-pink-950 rounded-2xl shadow-xl p-6 sm:p-8 border border-pink-200 dark:border-pink-800 flex-1 flex flex-col">
            <div className="flex items-center space-x-3 mb-6">
              <span className="text-4xl">{question.emoji}</span>
              <h2 className="text-xl sm:text-2xl font-bold text-gray-800 dark:text-gray-100">
                {question.question}
              </h2>
            </div>

            <div className="space-y-3 flex-1">
              {question.options.map((option, index) => {
                const isSelected = question.isCheckbox
                  ? Array.isArray(currentAnswer) && currentAnswer.includes(option.text)
                  : currentAnswer === option.value;

                return (
                  <button
                    key={index}
                    onClick={() =>
                      handleOptionClick(question.isCheckbox ? option.text : option.value)
                    }
                    className={`w-full text-left px-4 py-3 rounded-lg border-2 transition-all ${
                      isSelected
                        ? "border-pink-500 bg-pink-100 dark:bg-pink-900/30 text-gray-800 dark:text-gray-100 font-semibold"
                        : "border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-700 dark:text-gray-300 hover:border-pink-300 hover:bg-pink-50 dark:hover:bg-pink-950"
                    }`}
                  >
                    {!question.isCheckbox && (
                      <span className="font-semibold mr-2">{option.value}.</span>
                    )}
                    {question.isCheckbox && (
                      <span className="mr-2">{isSelected ? "☑" : "☐"}</span>
                    )}
                    {option.text}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Navigation */}
      <div className="flex justify-between mt-6">
        <button
          onClick={onPrevious}
          disabled={currentIndex === 0}
          className="px-6 py-3 bg-white dark:bg-gray-900 text-gray-700 dark:text-gray-300 font-semibold rounded-lg border-2 border-gray-300 dark:border-gray-700 hover:border-gray-400 dark:hover:border-gray-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Back
        </button>

        {isLast ? (
          <button
            onClick={onComplete}
            disabled={!isAnswered}
            className="px-6 py-3 bg-gradient-to-r from-orange-500 to-pink-500 text-white font-semibold rounded-lg hover:from-orange-600 hover:to-pink-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg"
          >
            Complete Quiz
          </button>
        ) : (
          <button
            onClick={onNext}
            disabled={!isAnswered}
            className="px-6 py-3 bg-gradient-to-r from-orange-500 to-pink-500 text-white font-semibold rounded-lg hover:from-orange-600 hover:to-pink-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg"
          >
            Next
          </button>
        )}
      </div>
    </div>
  );
}

const TIER_COLORS: Record<string, string> = {
  A: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300",
  B: "bg-sky-100 text-sky-700 dark:bg-sky-900/40 dark:text-sky-300",
  C: "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300",
  D: "bg-rose-100 text-rose-700 dark:bg-rose-900/40 dark:text-rose-300",
};

// ─── Resource Recommendation Engine ────────────────────────────────

interface ResourceRec {
  icon: React.ReactNode;
  title: string;
  description: string;
  path: string;
}

const CONDITION_RESOURCES: Record<string, { condition: string; learning: string; description: string }> = {
  Endometriosis: { condition: "/conditions/endometriosis", learning: "/research/learning/endometriosis-deep-dive", description: "Understand endo staging, treatment options, and how to advocate for care." },
  PCOS: { condition: "/conditions/pcos", learning: "/research/learning/pcos-fundamentals", description: "Hormonal drivers, insulin resistance, and evidence-based management." },
  PMDD: { condition: "/conditions/pmdd", learning: "/research/learning/pmdd-management", description: "Distinguish PMDD from PMS and explore treatment pathways." },
  Fibroids: { condition: "/conditions/fibroids", learning: "", description: "Types, symptoms, and when to consider treatment." },
  Adenomyosis: { condition: "/conditions/adenomyosis", learning: "", description: "Often misdiagnosed — learn what sets it apart from endo." },
  "Thyroid disorder": { condition: "/conditions/thyroid-disorders", learning: "", description: "How thyroid dysfunction affects cycles, mood, and energy." },
  "Interstitial cystitis": { condition: "/conditions/interstitial-cystitis", learning: "", description: "Bladder pain, urgency, and pelvic floor connections." },
};

function getResourceRecs(_quizSlug: string, results: QuizResults): ResourceRec[] {
  const recs: ResourceRec[] = [];
  const seen = new Set<string>();

  // Quiz-specific condition-based recs (from quizData checkbox answers stored in symptoms)
  // The "symptoms" in QuizResults are actually the checkbox selections (diagnoses, symptoms, etc.)
  for (const symptom of results.symptoms) {
    const match = CONDITION_RESOURCES[symptom];
    if (match && !seen.has(match.condition)) {
      seen.add(match.condition);
      if (match.learning) {
        recs.push({
          icon: <BookOpenText size={20} weight="fill" />,
          title: `${symptom} Learning Path`,
          description: match.description,
          path: match.learning,
        });
      } else {
        recs.push({
          icon: <FirstAidKit size={20} weight="fill" />,
          title: `${symptom} Guide`,
          description: match.description,
          path: match.condition,
        });
      }
    }
    if (recs.length >= 3) break;
  }

  // If the quiz is adrenal-fatigue or hormonal, add a general concern-based rec
  if (recs.length < 3) {
    // Check tier for severity-based suggestion
    if (results.tier.key === "C" || results.tier.key === "D") {
      if (!seen.has("/health/appointments")) {
        recs.push({
          icon: <FirstAidKit size={20} weight="fill" />,
          title: "Appointment Prep",
          description: "Bring organized data and targeted questions to your next doctor visit.",
          path: "/health/appointments",
        });
        seen.add("/health/appointments");
      }
    }
  }

  // Always offer the library as a fallback
  if (recs.length < 3 && !seen.has("/research")) {
    recs.push({
      icon: <BookOpenText size={20} weight="fill" />,
      title: "Research Library",
      description: "Evidence-based articles on women's health conditions, treatments, and more.",
      path: "/research",
    });
  }

  return recs.slice(0, 3);
}

function ResultsPhase({
  quiz,
  results,
  onRetake,
  onSwitchQuiz,
  isSignedIn,
  onShowLogin,
}: {
  quiz: QuizData;
  results: QuizResults;
  onRetake: () => void;
  onSwitchQuiz: () => void;
  isSignedIn: boolean;
  onShowLogin: () => void;
}) {
  const { tier, symptoms, breakdown } = results;
  const otherQuiz = QUIZZES.find((q) => q.slug !== quiz.slug);

  const [email, setEmail] = useState("");
  const [emailStatus, setEmailStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [emailError, setEmailError] = useState("");

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = email.trim();
    if (!trimmed || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      setEmailError("Please enter a valid email address.");
      return;
    }
    setEmailStatus("loading");
    setEmailError("");
    try {
      const headers: HeadersInit = { "Content-Type": "application/json" };
      const res = await fetch(apiUrl("/subscribe"), {
        method: "POST",
        headers,
        body: JSON.stringify({ email: trimmed, source: `quiz_${quiz.slug}`, quizResult: tier.key }),
      });
      const data = await res.json();
      if (data.success) {
        setEmailStatus("success");
      } else {
        setEmailError("That didn't go through — let's try again.");
        setEmailStatus("error");
      }
    } catch {
      setEmailError("That didn't go through — let's try again.");
      setEmailStatus("error");
    }
  };

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <div className="text-center mb-8">
        <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-800 dark:text-gray-100 mb-3">
          Your Results
        </h1>
        <p className="text-lg text-gray-600 dark:text-gray-400">
          Not a Diagnosis — A Direction
        </p>
      </div>

      {/* Result tier card */}
      <div className="bg-pink-50 dark:bg-pink-950 rounded-2xl shadow-xl p-8 mb-6 border border-pink-200 dark:border-pink-800">
        <div className="flex items-center space-x-3 mb-4">
          <span className="text-4xl">{tier.emoji}</span>
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-800 dark:text-gray-100">
            {tier.title}
          </h2>
        </div>
        <p className="text-lg text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
          {tier.summary}
        </p>
        <p className="text-gray-600 dark:text-gray-400 leading-relaxed">
          {tier.detail}
        </p>
      </div>

      {/* Symptom note */}
      {symptoms.length > 0 && tier.symptomNote && (
        <div className="bg-orange-50 dark:bg-orange-950 rounded-xl p-6 mb-6 border border-orange-200 dark:border-orange-800">
          <p className="text-gray-700 dark:text-gray-300">
            {formatSymptomNote(tier.symptomNote, symptoms)}
          </p>
        </div>
      )}

      {/* Answer breakdown */}
      <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-lg p-6 mb-6 border border-gray-200 dark:border-gray-700">
        <h3 className="text-lg font-bold text-gray-800 dark:text-gray-100 mb-3">
          Answer Breakdown
        </h3>
        <div className="flex flex-wrap gap-2">
          {(["D", "C", "B", "A"] as const).map((key) => {
            const count = breakdown[key];
            if (count === 0) return null;
            return (
              <span
                key={key}
                className={`px-3 py-1.5 rounded-full text-sm font-medium ${TIER_COLORS[key]}`}
              >
                {count} {key}{count > 1 ? "'s" : ""}
              </span>
            );
          })}
        </div>
      </div>

      {/* Next Steps */}
      <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-lg p-8 mb-6 border border-gray-200 dark:border-gray-700">
        <h3 className="text-2xl font-bold text-gray-800 dark:text-gray-100 mb-4">
          💬 What to Do Next
        </h3>
        <ul className="space-y-3 text-gray-700 dark:text-gray-300">
          {quiz.nextSteps.map((step, index) => (
            <li key={index} className="flex items-start">
              <CheckCircle
                size={20}
                className="text-pink-500 mr-3 mt-0.5 flex-shrink-0"
                weight="fill"
              />
              <span>{step}</span>
            </li>
          ))}
        </ul>
      </div>

      {/* Important Note */}
      {quiz.importantNote && (
        <div className="bg-orange-50 dark:bg-orange-950 rounded-xl p-6 mb-6 border border-orange-200 dark:border-orange-800">
          <p className="text-gray-700 dark:text-gray-300">
            <strong>Important reminder:</strong> {quiz.importantNote}
          </p>
        </div>
      )}

      {/* Sign-up / email capture — anonymous users only */}
      {!isSignedIn && (
        <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-lg p-8 mb-6 border border-gray-200 dark:border-gray-700 text-center">
          <h3 className="text-xl font-bold text-gray-800 dark:text-gray-100 mb-2">
            Get personalized insights based on your results
          </h3>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
            Create a free account to chat with Romy about your results, track symptoms, and get ongoing support.
          </p>
          <button
            onClick={onShowLogin}
            className="w-full px-6 py-3.5 bg-gradient-to-r from-orange-500 to-pink-500 text-white font-semibold rounded-xl hover:from-orange-600 hover:to-pink-600 transition-all shadow-lg mb-4"
          >
            Create Free Account
          </button>

          {emailStatus === "success" ? (
            <div className="flex items-center justify-center gap-2 py-3 px-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-xl">
              <CheckCircle size={20} weight="fill" className="text-green-500" />
              <span className="text-sm font-medium text-green-700 dark:text-green-300">Check your inbox!</span>
            </div>
          ) : (
            <form onSubmit={handleEmailSubmit}>
              <p className="text-xs text-gray-400 dark:text-gray-500 mb-2">or save your results via email</p>
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <EnvelopeSimple size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => { setEmail(e.target.value); setEmailError(""); }}
                    placeholder="your@email.com"
                    className="w-full pl-9 pr-3 py-3 text-sm bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-pink-300 dark:focus:ring-pink-600 placeholder:text-gray-400"
                  />
                </div>
                <button
                  type="submit"
                  disabled={emailStatus === "loading"}
                  className="px-5 py-3 bg-pink-500 text-white text-sm font-semibold rounded-xl hover:bg-pink-600 transition-all disabled:opacity-60 whitespace-nowrap"
                >
                  {emailStatus === "loading" ? "Sending..." : "Send"}
                </button>
              </div>
              {emailError && (
                <p className="text-xs text-red-500 dark:text-red-400 text-center mt-2">{emailError}</p>
              )}
            </form>
          )}
        </div>
      )}

      {/* Romy conversation starters */}
      <div className="bg-gradient-to-r from-orange-500 to-pink-500 rounded-2xl p-8 text-white mb-6">
        <div className="flex items-center justify-center space-x-2 mb-4">
          <Sparkle size={32} weight="fill" />
          <h3 className="text-2xl font-bold">Continue with Romy</h3>
        </div>
        <p className="text-center text-white/90 mb-6">
          Ask Romy follow-up questions based on your results
        </p>
        <div className="space-y-3">
          {quiz.romyStarters.map((starter, index) => {
            const filled = starter.replace("{tier}", tier.key);
            return (
              <Link
                key={index}
                to={`/chat?message=${encodeURIComponent(filled)}`}
                className="flex items-center justify-between px-4 py-3 bg-white/15 hover:bg-white/25 rounded-lg transition-all group"
              >
                <div className="flex items-center space-x-3">
                  <ChatCircle size={20} weight="fill" className="flex-shrink-0" />
                  <span className="text-sm sm:text-base">{filled}</span>
                </div>
                <ArrowRight
                  size={18}
                  weight="bold"
                  className="flex-shrink-0 opacity-60 group-hover:opacity-100 transition-opacity"
                />
              </Link>
            );
          })}
        </div>
      </div>

      {/* Recommended resources */}
      {(() => {
        const recs = getResourceRecs(quiz.slug, results);
        if (recs.length === 0) return null;
        return (
          <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-lg p-6 mb-6 border border-gray-200 dark:border-gray-700">
            <h3 className="text-lg font-bold text-gray-800 dark:text-gray-100 mb-4">
              Recommended for You
            </h3>
            <div className="space-y-3">
              {recs.map((rec) => (
                <Link
                  key={rec.path}
                  to={rec.path}
                  className="flex items-start gap-3 p-3 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors group"
                >
                  <div className="flex-shrink-0 w-9 h-9 rounded-lg bg-pink-50 dark:bg-pink-900/30 flex items-center justify-center text-pink-500">
                    {rec.icon}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-gray-800 dark:text-gray-100 group-hover:text-pink-600 dark:group-hover:text-pink-400 transition-colors">
                      {rec.title}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{rec.description}</p>
                  </div>
                  <ArrowRight size={16} className="text-gray-300 dark:text-gray-600 group-hover:text-pink-400 mt-1 flex-shrink-0 transition-colors" />
                </Link>
              ))}
            </div>
          </div>
        );
      })()}

      {/* Retake / Switch */}
      <div className="flex flex-col sm:flex-row gap-4 justify-center">
        <button
          onClick={onRetake}
          className="px-6 py-3 bg-white dark:bg-gray-900 text-gray-700 dark:text-gray-300 font-semibold rounded-lg border-2 border-gray-300 dark:border-gray-700 hover:border-gray-400 dark:hover:border-gray-600 transition-all"
        >
          Retake Quiz
        </button>
        {otherQuiz && (
          <button
            onClick={onSwitchQuiz}
            className="px-6 py-3 bg-white dark:bg-gray-900 text-gray-700 dark:text-gray-300 font-semibold rounded-lg border-2 border-gray-300 dark:border-gray-700 hover:border-gray-400 dark:hover:border-gray-600 transition-all"
          >
            Try: {otherQuiz.title}
          </button>
        )}
      </div>
    </div>
  );
}

// --- Main Component ---

export default function Quiz() {
  const { currentUser } = useAuth();
  const [showLogin, setShowLogin] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const typeParam = searchParams.get("type");

  const quiz = useMemo(() => (typeParam ? getQuizBySlug(typeParam) : undefined), [typeParam]);

  const [phase, setPhase] = useState<Phase>(() => {
    if (typeParam && getQuizBySlug(typeParam)) return "intro";
    return "picker";
  });

  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState<Map<number, string | string[]>>(new Map());
  const [results, setResults] = useState<QuizResults | null>(null);

  const selectQuiz = useCallback(
    (slug: string) => {
      setSearchParams({ type: slug });
      setPhase("intro");
      setCurrentIndex(0);
      setAnswers(new Map());
      setResults(null);
    },
    [setSearchParams]
  );

  const startQuiz = useCallback(() => {
    setPhase("questions");
  }, []);

  const goToPicker = useCallback(() => {
    setSearchParams({});
    setPhase("picker");
    setCurrentIndex(0);
    setAnswers(new Map());
    setResults(null);
  }, [setSearchParams]);

  const handleAnswer = useCallback(
    (questionId: number, value: string | string[]) => {
      setAnswers((prev) => {
        const next = new Map(prev);
        next.set(questionId, value);
        return next;
      });
    },
    []
  );

  const handleNext = useCallback(() => {
    setCurrentIndex((i) => i + 1);
  }, []);

  const handlePrevious = useCallback(() => {
    setCurrentIndex((i) => Math.max(0, i - 1));
  }, []);

  const handleComplete = useCallback(() => {
    if (!quiz) return;
    const r = calculateResults(quiz, answers);
    setResults(r);
    setPhase("results");
  }, [quiz, answers]);

  const handleRetake = useCallback(() => {
    setPhase("intro");
    setCurrentIndex(0);
    setAnswers(new Map());
    setResults(null);
  }, []);

  const handleSwitchQuiz = useCallback(() => {
    if (!quiz) return;
    const other = QUIZZES.find((q) => q.slug !== quiz.slug);
    if (other) selectQuiz(other.slug);
  }, [quiz, selectQuiz]);

  return (
    <div className="min-h-screen bg-gradient-to-b from-orange-50 to-white dark:from-gray-900 dark:to-gray-900">
      <PageMeta
        title={quiz ? `${quiz.title} — Free Health Quiz` : 'Free Women\'s Health Quizzes'}
        description={quiz ? quiz.description : 'Take our free self-check quizzes for endometriosis, PCOS, PMDD, perimenopause, thyroid, and more. Get personalized insights and next steps.'}
        path={quiz ? `/quiz?type=${quiz.slug}` : '/quiz'}
      />
      <JsonLd data={softwareAppSchema({ name: "Women's Health Quiz", description: "Free self-assessment quizzes to help you understand your symptoms and identify potential women's health conditions.", url: 'https://romyhealth.co/quiz' })} />
      <AppNav />

      <main className="pt-16 pb-16 md:pb-0 min-h-screen">
        {phase === "picker" && <PickerPhase onSelectQuiz={selectQuiz} />}

        {phase === "intro" && quiz && (
          <IntroPhase quiz={quiz} onStart={startQuiz} onBack={goToPicker} />
        )}

        {phase === "questions" && quiz && (
          <QuestionsPhase
            quiz={quiz}
            currentIndex={currentIndex}
            answers={answers}
            onAnswer={handleAnswer}
            onNext={handleNext}
            onPrevious={handlePrevious}
            onComplete={handleComplete}
          />
        )}

        {phase === "results" && quiz && results && (
          <ResultsPhase
            quiz={quiz}
            results={results}
            onRetake={handleRetake}
            onSwitchQuiz={handleSwitchQuiz}
            isSignedIn={!!currentUser}
            onShowLogin={() => setShowLogin(true)}
          />
        )}
      </main>

      <LoginModal isOpen={showLogin} onClose={() => setShowLogin(false)} />
    </div>
  );
}
